/**
 * MXlab — Configure ta déco (LOT 1).
 *
 * Responsabilités (aucune logique métier) :
 *
 * 1. Bascule d'état (choice | form | saved) via l'attribut `data-mx-state`, source
 *    unique ; le CSS affiche le panneau. Boutons `data-mx-goto`. Zéro rechargement.
 *
 * 2. Sélecteurs de police (groupe Pilote) : aperçu en direct (le texte saisi se
 *    rend dans chaque tuile ET la police choisie s'applique au champ), et dépliage
 *    des polices au-delà des 6 premières (`data-mx-fonts-more`). Les @font-face sont
 *    déclarés en Liquid ; seules les tuiles VISIBLES déclenchent le téléchargement.
 *
 * 3. Propreté des données panier. Champs et radios police partent en line item
 *    properties via l'attribut `form`. Au CLIC du bouton d'ajout (capture window,
 *    donc AVANT le submit et donc avant mx-options / le handler natif), on DÉSACTIVE :
 *    - les champs vides ; tous les champs si l'état ≠ form ;
 *    - les radios police dont le champ texte lié est vide (un nom vide n'a pas de
 *      police à annoncer). Réactivation au tick suivant (la FormData est déjà lue).
 *    Le champ FICHIER (data-mx-field) suit la même règle : `.value` vide s'il n'y a
 *    pas de fichier → désactivé → pas de propriété.
 *
 * 4. Upload MULTI-FICHIERS (logos) : N slots = N inputs fichier pré-rendus (une
 *    propriété indexée chacun). Le picker (multiple, non soumis) et le drop
 *    distribuent les fichiers aux slots via DataTransfer, avec COMPACTION (les
 *    fichiers occupent toujours les slots 1..K, pas de trou). Validation à la
 *    sélection : nombre max, taille par fichier, taille totale — messages clairs,
 *    rien n'attend le paiement. Liste + compteur + bascule zone/message plein.
 *    Survie au re-rendu native : le bloc n'est PAS morphé (seul <variant-picker>
 *    l'est), donc les slots (pré-rendus côté serveur) et leurs fichiers restent.
 */
class MxConfig extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;

    // 1 + dépliage. Clic : bascule d'état, ou « voir plus » de polices.
    this.onClick = (event) => {
      const goto = event.target.closest('[data-mx-goto]');
      if (goto && this.contains(goto)) {
        this.goToState(goto.dataset.mxGoto);
        return;
      }
      // « Changer de méthode » (bandeau du formulaire) : revient à l'état D'OÙ L'ON VIENT
      // (choix → depuis A, config enregistrée → depuis D), jamais un retour forcé au choix.
      const back = event.target.closest('[data-mx-form-back]');
      if (back && this.contains(back)) {
        this.goToState(this.formOrigin || 'choice');
        return;
      }
      const more = event.target.closest('[data-mx-fonts-more]');
      if (more && this.contains(more)) {
        const grid = more.previousElementSibling;
        if (grid && grid.hasAttribute('data-mx-fonts')) grid.classList.add('mx-fonts--expanded');
        more.hidden = true;
        this.fitAllTiles(); // ajuster les tuiles nouvellement affichées
        return;
      }
      const remove = event.target.closest('[data-mx-files-remove]');
      if (remove && this.contains(remove)) {
        const container = remove.closest('[data-mx-files]');
        const slot = document.getElementById(remove.dataset.slotId);
        if (slot) {
          // Vider est autorisé (seul ÉCRIRE un chemin de fichier dans .value est interdit).
          slot.value = '';
          // Recompacter les slots restants (pas de trou), puis réafficher.
          this.setSlots(container, this.currentFiles(container));
          this.renderFiles(container);
          this.showFilesError(container, []);
        }
      }
    };
    this.addEventListener('click', this.onClick);

    // 2a. Aperçu en direct : la frappe met à jour les tuiles de la même clé.
    this.onInput = (event) => {
      const field = event.target.closest('[data-mx-field][data-mx-key]');
      if (field) this.updateTiles(field.dataset.mxKey);
    };
    this.addEventListener('input', this.onInput);

    // 2b. Sélection d'une police : on l'applique au champ texte lié.
    //     Fichier choisi (dialogue) : on rafraîchit l'affichage nom/taille.
    this.onChange = (event) => {
      const radio = event.target.closest('[data-mx-font]');
      if (radio) {
        this.applyFont(radio.dataset.mxKey, radio.dataset.mxFamily);
        return;
      }
      const picker = event.target.closest('[data-mx-files-picker]');
      if (picker) {
        const container = picker.closest('[data-mx-files]');
        this.addFiles(container, picker.files);
        picker.value = ''; // le picker n'est pas soumis ; on le vide après distribution.
      }
    };
    this.addEventListener('change', this.onChange);

    // Entrée dans un input texte ne doit pas soumettre le formulaire panier :
    // sinon la soumission clavier court-circuiterait le clic bouton (donc le disable).
    this.onKeydown = (event) => {
      if (event.key === 'Enter' && event.target.matches('input[data-mx-field]')) {
        event.preventDefault();
      }
    };
    this.addEventListener('keydown', this.onKeydown);

    // 3. Désactivation avant l'ajout au panier.
    // INVARIANT PANIER (dépendance temporelle) — NE PAS CASSER :
    //   • ce handler écoute le CLIC en capture → il s'exécute AVANT le submit, donc avant
    //     que mx-options/le natif ne lise la FormData. La désactivation des vides doit rester
    //     SYNCHRONE ici (ne PAS rendre ce handler async / await).
    //   • la réactivation est en setTimeout(0) : elle tombe APRÈS la lecture de la FormData
    //     (tâche suivante). Ne PAS retirer ce setTimeout, ne PAS le remplacer par un await.
    //   Sinon : soit un champ vide repart au panier, soit un champ rempli est désactivé et perdu.
    this.onSubmitClick = (event) => {
      const btn = event.target.closest('button, input[type="submit"]');
      if (!btn || btn.type !== 'submit') return;
      const targetForm = btn.getAttribute('form') || (btn.form && btn.form.getAttribute('id'));
      if (targetForm !== this.formId) return;

      this.disableForSubmit();
      setTimeout(() => this.enableAll(), 0);
    };
    window.addEventListener('click', this.onSubmitClick, { capture: true });

    // 4. Zones d'upload multi-fichiers : glisser-déposer + état initial.
    for (const container of this.querySelectorAll('[data-mx-files]')) {
      const drop = container.querySelector('[data-mx-files-drop]');
      drop.addEventListener('dragover', (event) => {
        event.preventDefault();
        container.classList.add('mx-config__files--drag');
      });
      drop.addEventListener('dragleave', () => container.classList.remove('mx-config__files--drag'));
      drop.addEventListener('drop', (event) => {
        event.preventDefault();
        container.classList.remove('mx-config__files--drag');
        const dropped = event.dataTransfer && event.dataTransfer.files;
        if (dropped && dropped.length) this.addFiles(container, dropped);
      });
      this.renderFiles(container);
    }

    // 5. Ajustement de la taille des tuiles de police (le texte long doit tenir).
    //    Initial + après chargement des polices (métriques réelles) + à chaque changement de
    //    largeur du conteneur (ResizeObserver → couvre la fenêtre ET la bascule 1↔2 colonnes).
    requestAnimationFrame(() => this.fitAllTiles());
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.fitAllTiles());
      this.onFontsDone = () => this.fitAllTiles();
      document.fonts.addEventListener('loadingdone', this.onFontsDone);
    }
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => {
        clearTimeout(this.fitTimer);
        this.fitTimer = setTimeout(() => this.fitAllTiles(), 80);
      });
      for (const fields of this.querySelectorAll('.mx-config__fields')) this.ro.observe(fields);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.onSubmitClick, { capture: true });
    if (this.onFontsDone && document.fonts) document.fonts.removeEventListener('loadingdone', this.onFontsDone);
    if (this.ro) this.ro.disconnect();
  }

  /**
   * Bascule d'état de l'étape 1. En allant vers le formulaire, MÉMORISE l'état d'origine
   * (`formOrigin`) pour que « Changer de méthode » y revienne. Affiche la note « config
   * mise de côté » UNIQUEMENT en état D (formulaire ouvert depuis « saved »).
   */
  goToState(target) {
    if (target === 'form') this.formOrigin = this.dataset.mxState || 'choice';
    this.dataset.mxState = target;
    const note = this.querySelector('[data-mx-formnote]');
    if (note) note.hidden = !(target === 'form' && this.formOrigin === 'saved');
  }

  /** Met à jour toutes les tuiles d'une clé avec le texte saisi (ou le repli). */
  updateTiles(key) {
    const field = this.querySelector('[data-mx-field][data-mx-key="' + key + '"]');
    const value = field ? field.value.trim() : '';
    for (const tile of this.querySelectorAll('[data-mx-tile][data-mx-key="' + key + '"]')) {
      tile.textContent = value || tile.dataset.mxFallback || '';
      this.fitTile(tile);
    }
  }

  /**
   * Ajuste la taille de police d'une tuile pour que le texte y tienne : on réduit
   * PROGRESSIVEMENT jusqu'à un plancher lisible (12px), plutôt que déborder/tronquer sec.
   * Au-delà du plancher, l'ellipsis de `.mx-font__preview` tronque proprement.
   * La largeur du texte ∝ la font-size (glyphes + padding en em) → une seule passe suffit.
   */
  fitTile(textEl) {
    const preview = textEl.parentElement; // .mx-font__preview (clip + ellipsis)
    if (!preview) return;
    textEl.style.fontSize = ''; // repart de la taille CSS de base
    const avail = preview.clientWidth;
    if (avail <= 0) return; // tuile masquée (extra non dépliée) → on ne mesure pas
    const natural = preview.scrollWidth;
    if (natural <= avail + 1) return; // tient déjà
    const base = parseFloat(getComputedStyle(textEl).fontSize) || 0;
    if (!base) return;
    const FLOOR = 12; // px, plancher lisible
    const target = Math.max(FLOOR, (base * avail) / natural);
    textEl.style.fontSize = target.toFixed(2) + 'px';
  }

  fitAllTiles() {
    for (const t of this.querySelectorAll('[data-mx-tile]')) this.fitTile(t);
  }

  /** Applique la police choisie au champ texte lié (aperçu dans le champ). */
  applyFont(key, family) {
    const field = this.querySelector('input[data-mx-field][data-mx-key="' + key + '"]');
    if (field && family) field.style.fontFamily = "'" + family + "'";
  }

  /**
   * Désactive : champs vides ; tout si l'état ≠ form ; radios police dont le champ
   * texte lié (même clé) est vide.
   */
  disableForSubmit() {
    const inactive = this.dataset.mxState !== 'form';
    const emptyKeys = new Set();

    for (const field of this.querySelectorAll('input[data-mx-field], textarea[data-mx-field]')) {
      const empty = field.value.trim() === '';
      field.disabled = inactive || empty;
      if (empty && field.dataset.mxKey) emptyKeys.add(field.dataset.mxKey);
    }

    for (const radio of this.querySelectorAll('[data-mx-font]')) {
      radio.disabled = inactive || emptyKeys.has(radio.dataset.mxKey);
    }
  }

  enableAll() {
    for (const el of this.querySelectorAll('[data-mx-field], [data-mx-font]')) el.disabled = false;
  }

  /** Fichiers actuellement dans les slots (dans l'ordre des slots). */
  currentFiles(container) {
    const out = [];
    for (const slot of container.querySelectorAll('[data-mx-slot]')) {
      if (slot.files && slot.files[0]) out.push(slot.files[0]);
    }
    return out;
  }

  /** Réaffecte une liste de fichiers aux slots 1..K (compaction) ; vide le reste. */
  setSlots(container, files) {
    const slots = [...container.querySelectorAll('[data-mx-slot]')];
    slots.forEach((slot, i) => {
      if (i < files.length) {
        const dt = new DataTransfer();
        dt.items.add(files[i]);
        slot.files = dt.files;
      } else {
        slot.value = '';
      }
    });
  }

  /** Ajoute des fichiers (dialogue/drop) avec validation nombre + taille + total. */
  addFiles(container, fileList) {
    const files = this.currentFiles(container);
    const maxSlots = parseInt(container.dataset.maxSlots, 10) || 8;
    const maxBytes = (parseFloat(container.dataset.maxMb) || 5) * 1024 * 1024;
    const totalBytes = (parseFloat(container.dataset.totalMb) || 20) * 1024 * 1024;
    let total = files.reduce((sum, f) => sum + f.size, 0);
    const errors = [];

    for (const file of fileList) {
      if (files.length >= maxSlots) break; // plus de slot libre → on arrête
      if (file.size > maxBytes) {
        errors.push(this.fillMsg(container.dataset.msgMax, { nom: file.name, max: container.dataset.maxMb }));
        continue;
      }
      if (total + file.size > totalBytes) {
        errors.push(this.fillMsg(container.dataset.msgTotal, { total: container.dataset.totalMb }));
        continue;
      }
      files.push(file);
      total += file.size;
    }

    this.setSlots(container, files);
    this.renderFiles(container);
    this.showFilesError(container, errors);
  }

  /** Reconstruit la liste + le compteur ; bascule zone de dépôt / message « plein ». */
  renderFiles(container) {
    const slots = [...container.querySelectorAll('[data-mx-slot]')];
    const files = this.currentFiles(container);
    const maxSlots = parseInt(container.dataset.maxSlots, 10) || 8;
    const removeLabel = container.dataset.removeLabel || 'Retirer';

    // Propriété VISIBLE : noms d'origine (ordre des slots) ; vide s'il n'y a pas de
    // fichier (→ désactivée au submit → pas de propriété).
    const summary = container.querySelector('[data-mx-files-summary]');
    if (summary) {
      summary.value = files.length
        ? this.fillMsg(container.dataset.summaryTpl, { noms: files.map((f) => f.name).join(', '), n: files.length })
        : '';
    }

    const list = container.querySelector('[data-mx-files-list]');
    list.replaceChildren();
    for (const slot of slots) {
      const file = slot.files && slot.files[0];
      if (!file) continue;
      const li = document.createElement('li');
      li.className = 'mx-config__files-item';

      const info = document.createElement('span');
      info.className = 'mx-config__files-item-info';
      const name = document.createElement('span');
      name.className = 'mx-config__files-item-name';
      name.textContent = file.name;
      const size = document.createElement('span');
      size.className = 'mx-config__files-item-size';
      size.textContent = this.formatSize(file.size);
      info.append(name, size);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mx-config__files-remove';
      btn.setAttribute('data-mx-files-remove', '');
      btn.dataset.slotId = slot.id;
      btn.textContent = removeLabel;

      li.append(info, btn);
      list.appendChild(li);
    }

    const count = container.querySelector('[data-mx-files-count]');
    count.textContent = this.fillMsg(container.dataset.msgCount, { n: files.length, max: maxSlots });

    const full = files.length >= maxSlots;
    container.querySelector('[data-mx-files-drop]').hidden = full;
    container.querySelector('[data-mx-files-full]').hidden = !full;
  }

  /** Remplace {clé} par sa valeur dans un gabarit de message. */
  fillMsg(tpl, vars) {
    let out = tpl || '';
    for (const key in vars) out = out.split('{' + key + '}').join(vars[key]);
    return out;
  }

  /** Affiche (ou masque) les erreurs de validation sous la zone de dépôt. */
  showFilesError(container, errors) {
    const el = container.querySelector('[data-mx-files-error]');
    if (!errors || !errors.length) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.textContent = errors.join(' ');
    el.hidden = false;
  }

  formatSize(bytes) {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  }
}

if (!customElements.get('mx-config')) {
  customElements.define('mx-config', MxConfig);
}
